import Vue from 'vue';
import Component from 'vue-class-component';
import {DownloadHelper} from '../../../helpers/download';
import {Watch} from 'vue-property-decorator';
import {DataManager, Visualisation, Toolbar, Chart, FilterBar, CustomDynamicView} from 'wc-visualisation';
import {Table, Icon, Popover, pivotData, formatDate, FieldType, formatDollar} from 'wc-lib';
import {format, addDays} from 'date-fns';
import { url } from 'inspector';
import { CsvDownloadMonths, CsvDownloadYears, NemRegions } from '../config';
import { Field } from 'vee-validate';

@Component({
  template: require('./average-price-tables.html'),
  name: 'average-price-tables',
  components: {
    'vis-visualisation': Visualisation,
    'vis-toolbar': Toolbar,
    'wc-icon': Icon,
    'wc-table': Table,
    'vis-chart': Chart,
    'vis-filter-bar': FilterBar,
    'wc-popover': Popover
  }
})
export class NemAveragePriceTables extends Vue {
  data = null;
  annualData = null;
  years = CsvDownloadYears();
  months = CsvDownloadMonths;
  selectedYear = this.years[0];
  selectedMonth = this.months[addDays(new Date(), -1).getMonth()].value;
  periods = [
    { value: 'DAY', label: 'Daily' },
    { value: 'MONTH', label: 'Monthly' },
    { value: 'ANNUAL', label: 'Annual' }
  ];
  selectedPeriod = 'DAY';
  columnsMonth = [
    {
      title: 'Date',
      field: 'MONTHNUMBER',
      formatter: (value) => {
        return value.substring(0, 7);
      }
    },
    {
      title: 'Region',
      field: 'REGIONID',
      formatter: (value) => {
        return value.replace('1', '');
      }
    },
    {
      title: 'Average RRP',
      field: 'AVGRRP',
      fieldType: FieldType.Dollar
    },
    {
      title: 'Peak RRP',
      field: 'PEAKRRP',
      fieldType: FieldType.Dollar
    }
  ];
  columnsAnnual = [];
  columnsDay = [];

  get isDay() {
    return this.selectedPeriod === 'DAY';
  }

  get isMonth() {
    return this.selectedPeriod === 'MONTH';
  }

  get isAnnual() {
    return this.selectedPeriod === 'ANNUAL';
  }

  get dayCsvLink() {
    return `../../data/nem/averageprices/WEB_AVERAGE_PRICE_DAY_${this.selectedYear}${this.selectedMonth}.csv`;
  }

  get monthCsvLink() {
    return `../../data/nem/averageprices/WEB_AVERAGE_PRICE_MONTH_${this.selectedYear}${this.selectedMonth}.csv`;
  }

  get annualCsvLink() {
    return `../../data/nem/averageprices/WEB_AVERAGE_PRICE_ANNUAL.csv`;
  }

  get downloadLink() {
    if (this.isDay) 
      return this.dayCsvLink;
    else if (this.isMonth)
      return this.monthCsvLink;
    else
      return this.annualCsvLink; 
  }

  created() {
    this.fetchData();
  }

  @Watch('selectedPeriod')
  @Watch('selectedYear')
  @Watch('selectedMonth')
  async fetchData() {
    let dataId = null;
    if (this.isDay) {
      dataId = await DataManager.addEndPoint(this.dayCsvLink, undefined, undefined,undefined,'csv','yyyy-MM-ddTHH:mm:ss');
      let dynamicView = DataManager.getDynamicView(dataId, 'averagepriceday');
      (dynamicView as any).applySortCriteria(['SETTLEMENTDATE', 'REGIONID']);
      let data = dynamicView.data();

      if (data && data.length > 0) {
        data = pivotData(data, ['SETTLEMENTDATE'], 'REGIONID', 'AVGRRP', 'PEAKRRP');
        let columns = [{
          title: 'Date',
          field: 'SETTLEMENTDATE',
          formatter: (value) => {
            return value.substring(0, 10);
          }
        }];
        const firstRow = Object.keys(data[0]);
        for (const key of firstRow) {
          if (key.indexOf('AVGRRP') !== -1) {
            columns.push({
              title: [key.replace('1 AVGRRP', ''), 'RRP'],
              field: key,
              formatter: (value) => {
                return value !== 'N/A' ? formatDollar(value) : value;
              },
              cellStyle: (value, row, column) => 'right-column',
              headerClass: 'right-column'
            } as any);
          } else if (key.indexOf('PEAKRRP') !== -1) {
            columns.push({
              title: 'PEAK RRP',
              field: key,
              formatter: (value) => {
                return value !== 'N/A' ? formatDollar(value) : value;
              },
              cellStyle: (value, row, column) => 'right-column',
              headerClass: 'right-column'
            } as any);
          }
        }

        this.columnsDay = columns;   
      }
      this.data = data;

    } else if (this.isMonth) {
      dataId = await DataManager.addEndPoint(this.monthCsvLink, undefined, undefined,undefined,'csv');
      let dynamicView = DataManager.getDynamicView(dataId, 'averagepricemonth');
      dynamicView.applyFind({ 'MONTH': this.months.find(x=> x.value === this.selectedMonth).label.toUpperCase()});
      this.data = dynamicView.data();
    } else if (this.isAnnual) {
      dataId = await DataManager.addEndPoint(this.annualCsvLink,undefined, undefined,undefined,'csv');
      let dynamicView = DataManager.getDynamicView(dataId, 'averagepriceannual');
      let data = dynamicView.data();

      if (data && data.length > 0) {
        data = pivotData(data, ['YEAR'], 'REGIONID', 'AVGRRP');
        let columns = [{
          title: 'Financial Year',
          field: 'YEAR'
        }];
        const firstRow = Object.keys(data[0]);
        for (const key of firstRow) {
          if (key !== 'YEAR') {
            columns.push({
              title: key.replace('1 AVGRRP', ''),
              field: key,
              fieldType: FieldType.Dollar
            } as any);
          }
        }

        this.columnsAnnual = columns;
        this.annualData = data.map(function(x) {
          return { ...x, YEAR: (parseInt(x.YEAR)-1) + '-' + x.YEAR.slice(-2) };
        });
      }

      this.data = data;
    }
  }
  


  selectPeriod(period: string) {
    this.selectedPeriod = period;
    this.data = null;
  }


}


// WEBPACK FOOTER //
// src/views/electricity/nem/average-price-tables.ts